import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  DollarSign, Users, TrendingUp, Calendar, ArrowLeft, Loader2,
  Download, CheckCircle, Clock, User, Mail, Wallet, FileText
} from "lucide-react";

const PayrollPage = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [driversData, setDriversData] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    driver_id: "",
    amount: 0,
    period_start: "",
    period_end: "",
    notes: ""
  });
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState({ total: 0, count: 0 });

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [driversRes, payoutsRes, pendingRes] = await Promise.all([
        api.get(`/payroll/all-drivers?period=${period}`),
        api.get("/payroll/payouts?limit=20"),
        api.get("/payroll/payouts/pending-total")
      ]);
      setDriversData(driversRes.data);
      setPayouts(payoutsRes.data);
      setPendingPayouts({
        total: pendingRes.data.pending_total,
        count: pendingRes.data.pending_count
      });
    } catch (error) {
      toast.error("Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId) => {
    setSelectedDriver(driverId);
    setLoadingDetails(true);
    try {
      const response = await api.get(`/payroll/driver-summary/${driverId}?period=${period}`);
      setDriverDetails(response.data);
    } catch (error) {
      toast.error("Failed to load driver details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreatePayout = async () => {
    if (!payoutForm.amount || !payoutForm.period_start || !payoutForm.period_end) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmittingPayout(true);
    try {
      await api.post("/payroll/payouts", payoutForm);
      toast.success("Payout created successfully");
      setShowPayoutDialog(false);
      setPayoutForm({ driver_id: "", amount: 0, period_start: "", period_end: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create payout");
    } finally {
      setSubmittingPayout(false);
    }
  };

  const handleMarkPaid = async (payoutId) => {
    try {
      await api.put(`/payroll/payouts/${payoutId}/mark-paid`);
      toast.success("Payout marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark payout as paid");
    }
  };

  const openPayoutDialog = (driver) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    setPayoutForm({
      driver_id: driver.driver_id,
      amount: driver.total_earnings,
      period_start: weekAgo.toISOString().split("T")[0],
      period_end: now.toISOString().split("T")[0],
      notes: ""
    });
    setShowPayoutDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 lg:p-8" data-testid="payroll-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard?tab=drivers">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold">Driver Payroll</h1>
            <p className="text-muted-foreground">Commission tracking and payout management</p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Driver Pay", value: `$${driversData?.totals?.total_driver_pay?.toFixed(2) || 0}`, icon: Wallet, color: "text-green-500", bg: "bg-green-500/10" },
            { label: "Total Revenue", value: `$${driversData?.totals?.total_revenue?.toFixed(2) || 0}`, icon: TrendingUp, color: "text-blue-500", bg: "bg-blue-500/10" },
            { label: "Total Jobs", value: driversData?.totals?.total_jobs || 0, icon: Calendar, color: "text-purple-500", bg: "bg-purple-500/10" },
            { label: "Pending Payouts", value: `$${pendingPayouts.total?.toFixed(2) || 0}`, icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
          ].map((stat, i) => (
            <Card key={i} className="border border-black/[0.06]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Driver List */}
          <div className="lg:col-span-2">
            <Card className="border border-black/[0.06]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Driver Earnings ({period === "week" ? "This Week" : period === "month" ? "This Month" : "All Time"})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-black/[0.06]">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Driver</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Jobs</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Earnings</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {driversData?.drivers?.map((driver) => (
                        <tr 
                          key={driver.driver_id} 
                          className={`border-b border-black/[0.04] hover:bg-white/[0.02] cursor-pointer transition-colors ${
                            selectedDriver === driver.driver_id ? "bg-primary/5" : ""
                          }`}
                          onClick={() => fetchDriverDetails(driver.driver_id)}
                          data-testid={`driver-row-${driver.driver_id}`}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{driver.driver_name}</p>
                                <p className="text-xs text-muted-foreground">{driver.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-mono">{driver.jobs_count}</td>
                          <td className="py-3 px-4 text-right font-mono">${driver.total_revenue.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-mono text-green-500 font-semibold">${driver.total_earnings.toFixed(2)}</span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openPayoutDialog(driver); }}
                              data-testid={`payout-btn-${driver.driver_id}`}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              Payout
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Driver Details / Recent Payouts */}
          <div className="space-y-6">
            {/* Driver Details */}
            {selectedDriver && (
              <Card className="border border-primary/20 bg-primary/[0.02]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Driver Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDetails ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : driverDetails ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                          <User className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">{driverDetails.driver?.name}</p>
                          <p className="text-xs text-muted-foreground">{driverDetails.driver?.email}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-green-500/10">
                          <p className="text-xs text-muted-foreground">Earnings</p>
                          <p className="text-lg font-bold text-green-500">${driverDetails.total_earnings?.toFixed(2)}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-500/10">
                          <p className="text-xs text-muted-foreground">Jobs</p>
                          <p className="text-lg font-bold text-blue-500">{driverDetails.jobs_count}</p>
                        </div>
                      </div>

                      {driverDetails.recent_jobs?.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Recent Jobs</p>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {driverDetails.recent_jobs.slice(0, 5).map((job) => (
                              <div key={job.id} className="flex items-center justify-between text-sm p-2 rounded bg-white/[0.02]">
                                <div>
                                  <span className="font-mono text-primary text-xs">{job.booking_ref}</span>
                                  <p className="text-xs text-muted-foreground truncate max-w-[150px]">{job.pickup_location}</p>
                                </div>
                                <span className="font-mono text-green-500">${job.driver_pay?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            )}

            {/* Recent Payouts */}
            <Card className="border border-black/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Recent Payouts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {payouts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No payouts recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {payouts.slice(0, 5).map((payout) => (
                      <div key={payout.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02]">
                        <div>
                          <p className="font-medium text-sm">{payout.driver_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold">${payout.amount.toFixed(2)}</p>
                          {payout.status === "pending" ? (
                            <Button size="sm" variant="ghost" className="h-6 text-xs text-green-500" onClick={() => handleMarkPaid(payout.id)}>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Mark Paid
                            </Button>
                          ) : (
                            <Badge className="bg-green-500/15 text-green-400 text-[10px]">Paid</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Create Payout
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm({ ...payoutForm, amount: parseFloat(e.target.value) || 0 })}
                data-testid="payout-amount"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={payoutForm.period_start}
                  onChange={(e) => setPayoutForm({ ...payoutForm, period_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={payoutForm.period_end}
                  onChange={(e) => setPayoutForm({ ...payoutForm, period_end: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                placeholder="Bank transfer reference, etc."
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>Cancel</Button>
              <Button onClick={handleCreatePayout} disabled={submittingPayout} data-testid="confirm-payout">
                {submittingPayout ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Create Payout
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollPage;
